import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { GoogleMap, OverlayView, Polyline } from "@react-google-maps/api";
import { Box, Tooltip, makeStyles } from "@material-ui/core";
import { useGoogleMaps } from "../../../../../../context/GoogleMapsLoaderProvider";
import { IAppState } from "../../../../../../store";
import { statusColor, EVENT_CONFIG } from "../constants";
import { TrackingWindow, Vehicle } from "../types";

const FALLBACK_CENTER = { lat: 54.5, lng: -3 };

// ~11m — below this the map can't visually separate two points anyway, so treat
// start/end as "the same spot" and split their pins apart instead of stacking them.
const SAME_POINT_EPSILON_DEG = 0.0001;
function isSameCoord(a: [number, number], b: [number, number]) {
  return Math.abs(a[0] - b[0]) < SAME_POINT_EPSILON_DEG && Math.abs(a[1] - b[1]) < SAME_POINT_EPSILON_DEG;
}

const useStyles = makeStyles(() => ({
  loading: { display: "flex", alignItems: "center", justifyContent: "center", height: "100%" },

  pinCol: { display: "flex", flexDirection: "column", alignItems: "center" },
  pinLabel: {
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: 6,
    border: "1.5px solid #fff",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 4px rgba(0,0,0,0.35)",
  },
  pinTail: { width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", marginTop: -1 },

  markerRoot: { position: "relative" },
  markerCircle: {
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translate(-50%, -50%)",
    transition: "width 0.15s, height 0.15s",
    position: "relative",
  },
  // @keyframes need to live at the makeStyles top level (JSS at-rule), not nested in sx like v5.
  "@keyframes fleetSelectedPulse": {
    "0%": { transform: "scale(0.9)", opacity: 0.6 },
    "100%": { transform: "scale(1.5)", opacity: 0 },
  },
  selectedRing: {
    position: "absolute",
    top: -6,
    right: -6,
    bottom: -6,
    left: -6,
    borderRadius: "50%",
    border: "2px solid var(--primary-color)",
    opacity: 0.6,
    animation: "$fleetSelectedPulse 1.6s ease-out infinite",
  },
  markerDot: {
    borderRadius: "50%",
    border: "2px solid #fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
  },
  plateLabel: {
    position: "absolute",
    left: 0,
    transform: "translate(-50%, -100%)",
    padding: "2px 8px",
    borderRadius: 6,
    backgroundColor: "var(--brand-accent)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    pointerEvents: "none",
  },

  marker: { cursor: "pointer" },
  routePinAnchor: { transform: "translate(-50%, -100%)", cursor: "default" },
  routePinAnchorLeft: { transform: "translate(-100%, -100%)", cursor: "default" },
  routePinAnchorRight: { transform: "translate(0%, -100%)", cursor: "default" },
  tooltipBox: { fontSize: 12, lineHeight: 1.6 },
  tooltipTitle: { fontWeight: 700 },
  tooltipValue: { color: "#fca5a5", fontWeight: 600 },
  eventDot: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2.5px solid #fff",
    transform: "translate(-50%, -50%)",
    cursor: "pointer",
  },
}));

// Flag-style pin: rounded label with the text baked in, plus a small triangular tail pointing
// down. The parent OverlayView wraps this in translate(-50%,-100%), which anchors this block's
// bottom edge (the tail's tip) at the route coordinate — no absolute positioning needed here.
function RoutePin({ variant }: { variant: "start" | "end" }) {
  const classes = useStyles();
  const color = variant === "start" ? "#16a34a" : "#111827";
  const text = variant === "start" ? "Start" : "End";
  return (
    <Box className={classes.pinCol}>
      <Box className={classes.pinLabel} style={{ backgroundColor: color }}>
        {text}
      </Box>
      <Box className={classes.pinTail} style={{ borderTop: `7px solid ${color}` }} />
    </Box>
  );
}

function VehicleMarkerIcon({ status, selected, plate }: { status: keyof typeof statusColor; selected: boolean; plate?: string }) {
  const classes = useStyles();
  const color = statusColor[status];
  const size = selected ? 40 : 32;
  return (
    <Box className={classes.markerRoot}>
      <Box
        className={classes.markerCircle}
        style={{
          width: size,
          height: size,
          backgroundColor: selected ? "color-mix(in srgb, var(--primary-color) 18%, transparent)" : `${color}33`,
          border: selected ? "2.5px solid var(--primary-color)" : `1.5px solid ${color}`,
        }}
      >
        {selected && <Box className={classes.selectedRing} />}
        <Box
          className={classes.markerDot}
          style={{ width: size * 0.62, height: size * 0.62, backgroundColor: color, fontSize: size * 0.32 }}
        >
          ▲
        </Box>
      </Box>

      {selected && plate && (
        <Box className={classes.plateLabel} style={{ top: -size / 2 - 8 }}>
          {plate}
        </Box>
      )}
    </Box>
  );
}

interface MapModuleProps {
  vehicles: Vehicle[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  trackingWindow: TrackingWindow | undefined;
}

export function MapModule({ vehicles, selectedId, onSelect, trackingWindow }: MapModuleProps) {
  const classes = useStyles();
  const { isLoaded } = useGoogleMaps();
  const mapRef = useRef<google.maps.Map | null>(null);
  const organisation = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const orgGeometry = organisation?.address?.geometry;
  const defaultCenter = orgGeometry ? { lat: orgGeometry.latitude, lng: orgGeometry.longitude } : FALLBACK_CENTER;

  // Google Maps' Polyline renders outside the DOM/CSS cascade, so its strokeColor option needs
  // an actual resolved color, not a "var(--brand-accent)" string — read the org's live brand
  // colors off :root once rather than hardcoding the fixed navy/blue here.
  const { routeHaloColor, routeLineColor } = useMemo(() => {
    const rootStyle = getComputedStyle(document.documentElement);
    return {
      routeHaloColor: rootStyle.getPropertyValue("--brand-accent").trim() || "#1F3B73",
      routeLineColor: rootStyle.getPropertyValue("--primary-color").trim() || "#2563eb",
    };
  }, []);

  const handleOnLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const locatedVehicles = vehicles.filter((v): v is Vehicle & { lat: number; lng: number } => v.lat != null && v.lng != null);
  // While a vehicle is selected (from the list, or after picking a tracking-history window),
  // only its own marker shows — the rest of the fleet is hidden so the map reads as "this one
  // vehicle" rather than the whole fleet with one highlighted.
  const visibleVehicles = selectedId ? locatedVehicles.filter((v) => v.id === selectedId) : locatedVehicles;

  useEffect(() => {
    if (!mapRef.current) return;
    if (trackingWindow) {
      const bounds = new google.maps.LatLngBounds();
      trackingWindow.route.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
      mapRef.current.fitBounds(bounds, 80);
    } else if (selectedId) {
      const v = locatedVehicles.find((x) => x.id === selectedId);
      if (v) {
        mapRef.current.panTo({ lat: v.lat, lng: v.lng });
        mapRef.current.setZoom(13);
      }
    } else if (locatedVehicles.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      locatedVehicles.forEach((v) => bounds.extend({ lat: v.lat, lng: v.lng }));
      mapRef.current.fitBounds(bounds);
    }
  }, [selectedId, trackingWindow]);

  if (!isLoaded) {
    return <Box className={classes.loading}>Loading map…</Box>;
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "100%" }}
      options={{
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
      }}
      zoom={6}
      center={defaultCenter}
      onLoad={handleOnLoad}
    >
      {visibleVehicles.map((v) => (
        <OverlayView key={v.id} position={{ lat: v.lat, lng: v.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
          <Box className={classes.marker} onClick={() => onSelect(v.id === selectedId ? "" : v.id)}>
            <VehicleMarkerIcon status={v.status} selected={v.id === selectedId} plate={v.plate} />
          </Box>
        </OverlayView>
      ))}

      {trackingWindow && (
        <>
          {/* Halo underneath + solid line on top keeps the route legible over any map style */}
          <Polyline
            path={trackingWindow.route.map(([lat, lng]) => ({ lat, lng }))}
            options={{ strokeColor: routeHaloColor, strokeOpacity: 0.35, strokeWeight: 9, zIndex: 1 }}
          />
          <Polyline
            path={trackingWindow.route.map(([lat, lng]) => ({ lat, lng }))}
            options={{ strokeColor: routeLineColor, strokeOpacity: 0.95, strokeWeight: 4, zIndex: 2 }}
          />
          {(() => {
            const startCoord = trackingWindow.route[0];
            const endCoord = trackingWindow.route[trackingWindow.route.length - 1];
            // Same spot (vehicle parked the whole window): splay the two pins apart
            // instead of stacking them exactly on top of each other.
            const overlap = isSameCoord(startCoord, endCoord);
            const startClass = overlap ? classes.routePinAnchorLeft : classes.routePinAnchor;
            const endClass = overlap ? classes.routePinAnchorRight : classes.routePinAnchor;
            return (
              <>
                <OverlayView
                  position={{ lat: startCoord[0], lng: startCoord[1] }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Tooltip title="Route start" placement="top">
                    <Box className={startClass}>
                      <RoutePin variant="start" />
                    </Box>
                  </Tooltip>
                </OverlayView>
                <OverlayView
                  position={{ lat: endCoord[0], lng: endCoord[1] }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Tooltip title="Route end (current position)" placement="top">
                    <Box className={endClass}>
                      <RoutePin variant="end" />
                    </Box>
                  </Tooltip>
                </OverlayView>
              </>
            );
          })()}
          {trackingWindow.events.map((e, i) => {
            const cfg = EVENT_CONFIG[e.type];
            return (
              <OverlayView key={i} position={{ lat: e.lat, lng: e.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                <Tooltip
                  title={
                    <Box className={classes.tooltipBox}>
                      <Box className={classes.tooltipTitle}>{cfg.label}</Box>
                      <Box>{e.date}</Box>
                      <Box>{e.time}</Box>
                      {e.value && <Box className={classes.tooltipValue}>{e.value}</Box>}
                    </Box>
                  }
                  placement="top"
                >
                  <Box
                    className={classes.eventDot}
                    style={{ backgroundColor: cfg.color, boxShadow: `0 0 0 3px ${cfg.color}4d, 0 2px 4px rgba(0,0,0,0.35)` }}
                  />
                </Tooltip>
              </OverlayView>
            );
          })}
        </>
      )}
    </GoogleMap>
  );
}
